import type { SessionView } from '@rummy/protocol';

export interface PersonalStats {
  hands: number;
  wins: number;
  drops: number;
  totalPoints: number;
  sessions: number;
  recorded: string[];
  /** Small, private milestones. They never appear to other players. */
  achievements: Achievement[];
  /** Consecutive completed sessions with zero hand wins. Resets on any win.
   *  Not a stat anyone else sees — it exists only to notice a rough patch. */
  lossStreak: number;
}

export type Achievement = 'first-win' | 'five-wins' | 'steady-player' | 'calm-table';

const KEY = 'rummy.personal-stats.v1';
const EMPTY: PersonalStats = {
  hands: 0, wins: 0, drops: 0, totalPoints: 0, sessions: 0, recorded: [], achievements: [], lossStreak: 0,
};

export function readPersonalStats(): PersonalStats {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<PersonalStats>;
    return {
      ...EMPTY,
      ...raw,
      recorded: raw.recorded ?? [],
      achievements: (raw.achievements ?? []).filter(isAchievement),
    };
  } catch {
    return EMPTY;
  }
}

/** Records a completed session once, locally, without a new profile-data surface. */
export function recordPersonalSession(roomId: string, session: SessionView, accountId: string): PersonalStats {
  const fingerprint = `${roomId}:${session.format}:${session.history
    .map((hand) => `${hand.ordinal}-${hand.results.map((result) => `${result.player}:${result.outcome}:${result.points}`).join(',')}`)
    .join('|')}`;
  const current = readPersonalStats();
  if (current.recorded.includes(fingerprint)) return current;

  let hands = 0;
  let wins = 0;
  let drops = 0;
  let totalPoints = 0;
  for (const hand of session.history) {
    const result = hand.results.find((candidate) => candidate.player === accountId);
    if (!result) continue;
    hands++;
    wins += result.outcome === 'won' ? 1 : 0;
    drops += ['first-drop', 'middle-drop', 'auto-drop'].includes(result.outcome) ? 1 : 0;
    totalPoints += result.points;
  }

  const next: PersonalStats = {
    hands: current.hands + hands,
    wins: current.wins + wins,
    drops: current.drops + drops,
    totalPoints: current.totalPoints + totalPoints,
    sessions: current.sessions + 1,
    recorded: [...current.recorded, fingerprint].slice(-32),
    achievements: unlockAchievements(current, { hands, wins, drops }),
    lossStreak: wins > 0 ? 0 : current.lossStreak + 1,
  };
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* Private browsing keeps the current session view without persistence. */
  }
  return next;
}

function isAchievement(value: unknown): value is Achievement {
  return value === 'first-win' || value === 'five-wins' || value === 'steady-player' || value === 'calm-table';
}

function unlockAchievements(
  current: PersonalStats,
  session: { hands: number; wins: number; drops: number },
): Achievement[] {
  const achievements = new Set(current.achievements);
  const totalWins = current.wins + session.wins;
  const totalSessions = current.sessions + 1;
  if (totalWins >= 1) achievements.add('first-win');
  if (totalWins >= 5) achievements.add('five-wins');
  if (totalSessions >= 3) achievements.add('steady-player');
  if (session.hands >= 3 && session.drops === 0) achievements.add('calm-table');
  return [...achievements];
}

export interface AchievementProgress {
  achievement: 'first-win' | 'five-wins' | 'steady-player';
  remaining: number;
}

/**
 * The closest locked, countable achievement — a reason to play the *next*
 * hand rather than only a record of the last one. `calm-table` is left out:
 * it depends on how a session goes, not a count that ticks toward it, so
 * "N away" would not mean anything.
 *
 * Returns null once nothing countable is left locked, or when the nearest
 * one is still far enough off that naming it would read as noise rather
 * than encouragement.
 */
export function nextAchievementProgress(stats: PersonalStats): AchievementProgress | null {
  const unlocked = new Set(stats.achievements);
  const candidates: AchievementProgress[] = [];
  if (!unlocked.has('first-win')) {
    candidates.push({ achievement: 'first-win', remaining: 1 });
  }
  if (!unlocked.has('five-wins')) {
    candidates.push({ achievement: 'five-wins', remaining: Math.max(1, 5 - stats.wins) });
  }
  if (!unlocked.has('steady-player')) {
    candidates.push({ achievement: 'steady-player', remaining: Math.max(1, 3 - stats.sessions) });
  }
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => a.remaining - b.remaining);
  const nearest = candidates[0]!;
  return nearest.remaining <= 3 ? nearest : null;
}
